import React, { useEffect, useState } from 'react';
import {
  MessageBar,
  MessageBarType,
  Stack,
  Text,
} from '@fluentui/react';
import moment from 'moment';
import { useAuth0 } from '@auth0/auth0-react';
import {
  formatBillitMoney,
  getLinkLinkedAmount,
  normalizePurchaseOrderLinks,
} from '../../billit-invoices/billitLinkAmounts';
import { BillitPaymentLabel, BillitStatusLabel } from '../../billit-invoices/billitDetailUi';
import { BillitPurchaseOrderLink } from '../../billit-invoices/types';

type Props = {
  purchaseOrderId?: number;
  isFullyLinked?: boolean;
};

const PurchaseOrderBillitLinks = ({ purchaseOrderId, isFullyLinked }: Props) => {
  const { getAccessTokenSilently } = useAuth0();
  const [links, setLinks] = useState<BillitPurchaseOrderLink[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchLinks = async () => {
      if (!purchaseOrderId) {
        setLinks([]);
        return;
      }
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URI}/purchase-orders/${purchaseOrderId}/billit-links`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );
        if (response.ok) {
          setLinks(normalizePurchaseOrderLinks(await response.json()));
        } else {
          setLinks([]);
        }
      } catch {
        setLinks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLinks();
  }, [purchaseOrderId, getAccessTokenSilently]);

  if (!purchaseOrderId) return null;

  const activeLinks = links.filter(link => !link.unlinked_at);

  const renderLinkEvent = (link: BillitPurchaseOrderLink) => {
    const isActive = !link.unlinked_at;
    const inv = link.billit_invoice;
    const label = inv?.order_number || (inv?.billit_order_id ? `#${inv.billit_order_id}` : `#${link.billit_invoice_id}`);

    return (
      <div
        key={`${link.id}-${isActive ? 'active' : 'history'}`}
        style={{
          border: '1px solid #E1E1E1',
          borderRadius: 1,
          padding: 10,
          background: isActive ? '#fafafa' : '#f3f2f1',
          opacity: isActive ? 1 : 0.85,
        }}
      >
        <Stack tokens={{ childrenGap: 6 }}>
          <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
            <Text style={{ fontWeight: 600 }}>
              {isActive ? 'Gekoppeld' : 'Ontkoppeld'} — {label}
            </Text>
            <Text style={{ fontSize: 11, color: '#666' }}>
              {moment(isActive ? link.linked_at : link.unlinked_at).format('DD/MM/YYYY HH:mm')}
            </Text>
          </Stack>
          {getLinkLinkedAmount(link) != null && (
            <Text style={{ fontSize: 12, color: '#444' }}>
              Bedrag: {formatBillitMoney(getLinkLinkedAmount(link), 'EUR')}
            </Text>
          )}
          {isActive && (
            <Stack horizontal tokens={{ childrenGap: 6 }}>
              {link.marks_invoice_fully_linked && (
                <span style={{ fontSize: 10, background: '#deecf9', padding: '2px 6px' }}>
                  Factuur volledig
                </span>
              )}
              {link.marks_purchase_order_fully_linked && (
                <span style={{ fontSize: 10, background: '#dff6dd', padding: '2px 6px' }}>
                  Bestelbon volledig
                </span>
              )}
            </Stack>
          )}
          {link.link_comment && (
            <Text style={{ fontSize: 12, color: '#444' }}>{link.link_comment}</Text>
          )}
          {!isActive && link.unlink_comment && (
            <Text style={{ fontSize: 12, color: '#666' }}>Ontkoppel: {link.unlink_comment}</Text>
          )}
        </Stack>
      </div>
    );
  };

  return (
    <Stack tokens={{ childrenGap: 12 }} style={{ padding: '0 24px 24px 24px', width: '100%' }}>
      <h3 style={{ margin: 0 }}>Billit facturen</h3>
      {isFullyLinked && (
        <MessageBar messageBarType={MessageBarType.info}>
          Deze bestelbon is gemarkeerd als volledig gekoppeld.
        </MessageBar>
      )}
      {loading && <Text style={{ color: '#666' }}>Laden...</Text>}
      {!loading && activeLinks.length === 0 && links.length === 0 && (
        <Text style={{ color: '#666' }}>Nog geen Billit-facturen gekoppeld.</Text>
      )}
      {!loading && activeLinks.length > 0 && (
        <Stack tokens={{ childrenGap: 8 }}>
          <Text style={{ fontWeight: 600, fontSize: 13 }}>Actieve koppelingen</Text>
          {activeLinks.map(link => {
            const inv = link.billit_invoice;
            const label =
              inv?.order_number ||
              (inv?.billit_order_id ? `#${inv.billit_order_id}` : `#${link.billit_invoice_id}`);
            return (
              <div
                key={link.id}
                style={{
                  border: '1px solid #E1E1E1',
                  borderRadius: 1,
                  padding: 12,
                  backgroundColor: '#fafafa',
                }}
              >
                <Text style={{ fontWeight: 600 }}>{label}</Text>
                {inv && (
                  <Stack
                    horizontal
                    tokens={{ childrenGap: 6 }}
                    verticalAlign='center'
                    style={{ marginTop: 6 }}
                  >
                    <BillitStatusLabel status={inv.order_status} />
                    <BillitPaymentLabel invoice={inv} />
                  </Stack>
                )}
              </div>
            );
          })}
        </Stack>
      )}
      {!loading && links.length > 0 && (
        <Stack tokens={{ childrenGap: 8 }}>
          <Text style={{ fontWeight: 600, fontSize: 13 }}>Koppelingshistoriek</Text>
          {links.map(renderLinkEvent)}
        </Stack>
      )}
    </Stack>
  );
};

export default PurchaseOrderBillitLinks;
